/**
 * La barra di paginazione, una sola per tutto il portale.
 *
 * Viveva scritta a mano dentro il Portafoglio di M1, e il Magazzino accanto non
 * ne aveva nessuna: mostrava «i 25 che sbloccano più commesse su 315» e gli altri
 * 290 non erano raggiungibili se non indovinando una ricerca.
 * Due liste nella stessa pagina che si sfogliano in due modi diversi sono due
 * cose da imparare invece di una.
 *
 * Dice **tre** numeri, non uno: a che pagina sei, quali righe stai guardando e
 * quante sono in tutto. «Pagina 2 di 13» da solo non dice quanto manca; «26–50 di
 * 315» da solo non dice dove sei.
 *
 * Estratta il 19-08-2026, quando il Magazzino ha avuto la sua.
 *
 * Si nasconde da sé quando c'è una pagina sola — una barra che dice sempre
 * «pagina 1 di 1» è rumore.
 */

interface PaginazioneProps {
  /** Indice della pagina corrente, **da 0** (come lo tiene chi la usa). */
  pagina: number;
  /** Quante righe stanno in una pagina. */
  perPagina: number;
  /** Il totale delle righe filtrate, non quello assoluto: è il denominatore che
   *  la persona vede davanti a sé. */
  totale: number;
  /** Il nome della cosa contata, al plurale: «commesse», «articoli». */
  unita: string;
  onVai: (pagina: number) => void;
  /** Formattatore dei numeri grandi (il portale raggruppa a mano: vedi `lib/numeri`). */
  formatta?: (n: number) => string;
}

export function Paginazione({
  pagina,
  perPagina,
  totale,
  unita,
  onVai,
  formatta = (n) => String(n),
}: PaginazioneProps) {
  const pagine = Math.max(1, Math.ceil(totale / perPagina));
  if (pagine <= 1) return null;

  const da = pagina * perPagina + 1;
  const a = Math.min((pagina + 1) * perPagina, totale);

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2 text-xs text-muted-foreground">
      <span>
        Pagina {pagina + 1} di {pagine} · {formatta(da)}–{formatta(a)} di {formatta(totale)} {unita}
      </span>
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => onVai(Math.max(0, pagina - 1))}
          disabled={pagina === 0}
          className="rounded border px-2 py-1 hover:bg-accent/40 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Precedente
        </button>
        <button
          type="button"
          onClick={() => onVai(Math.min(pagine - 1, pagina + 1))}
          disabled={pagina >= pagine - 1}
          className="rounded border px-2 py-1 hover:bg-accent/40 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Successiva
        </button>
      </div>
    </div>
  );
}
